import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'
import { feedbackAPI } from '../api/client'
import SellPathCardIcon from './SellPathCardIcon'

const MAX_ATTACHMENTS = 5
const MAX_FILE_BYTES = 5 * 1024 * 1024
const ACCEPT_TYPES =
  'image/jpeg,image/png,image/webp,image/gif,application/pdf,.jpg,.jpeg,.png,.webp,.gif,.pdf'

const RECOMMEND_OPTIONS = ['yes', 'unsure', 'no'] as const
const USE_OPTIONS = ['buy', 'sell', 'browse', 'imei'] as const

type RecommendOption = (typeof RECOMMEND_OPTIONS)[number]
type UseOption = (typeof USE_OPTIONS)[number]

type Attachment = {
  id: string
  file: File
  previewUrl: string | null
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2.5l2.86 5.8 6.4.93-4.63 4.52 1.09 6.37L12 17.9l-5.72 3.02 1.09-6.37L2.74 9.23l6.4-.93L12 2.5z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function isAllowedAttachment(file: File): boolean {
  const type = file.type.toLowerCase()
  if (/^image\/(jpeg|png|webp|gif)$/.test(type) || type === 'application/pdf') return true
  return /\.(jpe?g|png|webp|gif|pdf)$/i.test(file.name)
}

function mapSubmitError(t: (key: string) => string, err: unknown): string {
  const detail = (err as AxiosError<{ detail?: string }>)?.response?.data?.detail
  if (detail === 'file_too_large') return t('footer.survey.attachSizeError')
  if (detail === 'unsupported_file_type') return t('footer.survey.attachTypeError')
  if (typeof detail === 'string' && detail.startsWith('max_files:')) return t('footer.survey.attachMaxError')
  return t('footer.survey.submitError')
}

export default function FeedbackSurveyStrip() {
  const { t } = useTranslation()
  const fileInputId = useId()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const attachmentsRef = useRef<Attachment[]>([])
  const [stars, setStars] = useState(0)
  const [hover, setHover] = useState(0)
  const [message, setMessage] = useState('')
  const [easeScore, setEaseScore] = useState<number | null>(null)
  const [recommend, setRecommend] = useState<RecommendOption | null>(null)
  const [primaryUse, setPrimaryUse] = useState<UseOption | null>(null)
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [submitted, setSubmitted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  attachmentsRef.current = attachments

  useEffect(() => {
    return () => {
      for (const item of attachmentsRef.current) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
      }
    }
  }, [])

  function addFiles(fileList: FileList | null) {
    if (!fileList?.length) return
    setError(null)

    const next = [...attachments]
    for (const file of Array.from(fileList)) {
      if (next.length >= MAX_ATTACHMENTS) {
        setError(t('footer.survey.attachMaxError'))
        break
      }
      if (!isAllowedAttachment(file)) {
        setError(t('footer.survey.attachTypeError'))
        continue
      }
      if (file.size > MAX_FILE_BYTES) {
        setError(t('footer.survey.attachSizeError'))
        continue
      }
      const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      next.push({ id: `${file.name}-${file.size}-${Date.now()}`, file, previewUrl })
    }
    setAttachments(next)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function buildSubmissionMessage(): string {
    const parts: string[] = []
    const trimmed = message.trim()
    if (trimmed) parts.push(trimmed)

    const quizLines: string[] = []
    if (easeScore != null) {
      quizLines.push(`${t('footer.survey.qEase')}: ${easeScore}/5`)
    }
    if (recommend) {
      quizLines.push(`${t('footer.survey.qRecommend')}: ${t(`footer.survey.recommend${recommend.charAt(0).toUpperCase()}${recommend.slice(1)}`)}`)
    }
    if (primaryUse) {
      quizLines.push(`${t('footer.survey.qUse')}: ${t(`footer.survey.use${primaryUse.charAt(0).toUpperCase()}${primaryUse.slice(1)}`)}`)
    }
    if (quizLines.length > 0) {
      parts.push([t('footer.survey.questionnaireHeader'), ...quizLines].join('\n'))
    }
    return parts.join('\n\n')
  }

  function removeAttachment(id: string) {
    setAttachments((current) => {
      const removed = current.find((item) => item.id === id)
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl)
      return current.filter((item) => item.id !== id)
    })
    setError(null)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (stars < 1) {
      setError(t('footer.survey.errorRating'))
      return
    }
    const submissionMessage = buildSubmissionMessage()
    if (!submissionMessage.trim()) {
      setError(t('footer.survey.errorContent'))
      return
    }

    setBusy(true)
    setError(null)
    try {
      await feedbackAPI.submit(
        stars,
        submissionMessage,
        attachments.map((item) => item.file),
      )
      setSubmitted(true)
    } catch (err) {
      setError(mapSubmitError(t, err))
    } finally {
      setBusy(false)
    }
  }

  const activeStars = hover || stars

  return (
    <article className="vc-sell-banner-card feedback-survey-strip vc-promo-duo__item" aria-labelledby="feedback-survey-title">
      <div className="vc-sell-banner-visual" aria-hidden="true">
        <SellPathCardIcon kind="promo-feedback" />
      </div>
      <div className="vc-sell-banner-inner">
        <span className="vc-sell-banner-badge">
          {t('footer.survey.chipTime')} · {t('footer.survey.chipAnon')}
        </span>
        <h3 id="feedback-survey-title" className="vc-sell-banner-title">
          {t('footer.survey.formTitle')}
        </h3>

        {submitted ? (
          <p className="vc-sell-banner-text feedback-survey-strip__success" role="status">
            {t('footer.survey.success')}
          </p>
        ) : (
          <form className="feedback-survey-form" onSubmit={submit} noValidate>
            <div className="feedback-survey-form__field">
              <span className="feedback-survey-form__label" id="feedback-survey-rating-label">
                {t('footer.survey.ratingLabel')}
              </span>
              <p className="feedback-survey-form__hint">{t('footer.survey.ratingHint')}</p>
              <div
                className="feedback-survey-form__stars"
                role="radiogroup"
                aria-labelledby="feedback-survey-rating-label"
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`feedback-survey-form__star ${n <= activeStars ? 'feedback-survey-form__star--on' : ''}`}
                    onClick={() => {
                      setStars(n)
                      setError(null)
                    }}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    aria-label={t('footer.survey.starAria', { stars: n })}
                    aria-checked={stars === n}
                    role="radio"
                    disabled={busy}
                  >
                    <StarIcon filled={n <= activeStars} />
                  </button>
                ))}
              </div>
            </div>

            <div className="feedback-survey-form__field feedback-survey-form__field--message">
              <label className="feedback-survey-form__label" htmlFor="feedback-survey-message">
                {t('footer.survey.messageLabel')}
              </label>
              <textarea
                id="feedback-survey-message"
                className="feedback-survey-form__input"
                rows={2}
                maxLength={2000}
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value)
                  setError(null)
                }}
                placeholder={t('footer.survey.messagePlaceholder')}
                disabled={busy}
              />
            </div>

            <div className="feedback-survey-form__field feedback-survey-form__field--attach">
              <span className="feedback-survey-form__label">{t('footer.survey.attachLabel')}</span>
              <p className="feedback-survey-form__hint">{t('footer.survey.attachHint')}</p>
              <input
                ref={fileInputRef}
                id={fileInputId}
                className="feedback-survey-form__file-input"
                type="file"
                accept={ACCEPT_TYPES}
                multiple
                onChange={(e) => addFiles(e.target.files)}
                disabled={busy || attachments.length >= MAX_ATTACHMENTS}
              />
              <label htmlFor={fileInputId} className="feedback-survey-form__attach-btn">
                {t('footer.survey.attachAdd')}
              </label>

              {attachments.length > 0 ? (
                <ul className="feedback-survey-form__attachments" aria-live="polite">
                  {attachments.map((item) => (
                    <li key={item.id} className="feedback-survey-form__attachment">
                      {item.previewUrl ? (
                        <img src={item.previewUrl} alt="" className="feedback-survey-form__attachment-thumb" />
                      ) : (
                        <span className="feedback-survey-form__attachment-file" aria-hidden="true">
                          PDF
                        </span>
                      )}
                      <span className="feedback-survey-form__attachment-name">{item.file.name}</span>
                      <button
                        type="button"
                        className="feedback-survey-form__attachment-remove"
                        onClick={() => removeAttachment(item.id)}
                        aria-label={t('footer.survey.attachRemove', { name: item.file.name })}
                        disabled={busy}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <aside className="feedback-survey-form__quiz" aria-label={t('footer.survey.quizTitle')}>
              <span className="feedback-survey-form__quiz-title">{t('footer.survey.quizTitle')}</span>

              <div className="feedback-survey-form__quiz-row">
                <span className="feedback-survey-form__quiz-q" id="feedback-survey-ease-label">
                  {t('footer.survey.qEase')}
                </span>
                <div
                  className="feedback-survey-form__quiz-ease"
                  role="radiogroup"
                  aria-labelledby="feedback-survey-ease-label"
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`feedback-survey-form__quiz-opt feedback-survey-form__quiz-opt--score${
                        easeScore === n ? ' feedback-survey-form__quiz-opt--on' : ''
                      }`}
                      onClick={() => {
                        setEaseScore(n)
                        setError(null)
                      }}
                      aria-checked={easeScore === n}
                      role="radio"
                      disabled={busy}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div className="feedback-survey-form__quiz-row">
                <span className="feedback-survey-form__quiz-q" id="feedback-survey-recommend-label">
                  {t('footer.survey.qRecommend')}
                </span>
                <div
                  className="feedback-survey-form__quiz-options"
                  role="radiogroup"
                  aria-labelledby="feedback-survey-recommend-label"
                >
                  {RECOMMEND_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`feedback-survey-form__quiz-opt${
                        recommend === option ? ' feedback-survey-form__quiz-opt--on' : ''
                      }`}
                      onClick={() => {
                        setRecommend(option)
                        setError(null)
                      }}
                      aria-checked={recommend === option}
                      role="radio"
                      disabled={busy}
                    >
                      {t(`footer.survey.recommend${option.charAt(0).toUpperCase()}${option.slice(1)}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="feedback-survey-form__quiz-row">
                <span className="feedback-survey-form__quiz-q" id="feedback-survey-use-label">
                  {t('footer.survey.qUse')}
                </span>
                <div
                  className="feedback-survey-form__quiz-options"
                  role="radiogroup"
                  aria-labelledby="feedback-survey-use-label"
                >
                  {USE_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`feedback-survey-form__quiz-opt${
                        primaryUse === option ? ' feedback-survey-form__quiz-opt--on' : ''
                      }`}
                      onClick={() => {
                        setPrimaryUse(option)
                        setError(null)
                      }}
                      aria-checked={primaryUse === option}
                      role="radio"
                      disabled={busy}
                    >
                      {t(`footer.survey.use${option.charAt(0).toUpperCase()}${option.slice(1)}`)}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {error ? (
              <p className="feedback-survey-form__error" role="alert">
                {error}
              </p>
            ) : null}

            <button type="submit" className="vc-sell-banner-cta feedback-survey-form__submit" disabled={busy}>
              <span>{busy ? t('footer.survey.submitting') : t('footer.survey.submit')}</span>
              {!busy ? (
                <span className="vc-sell-banner-cta-arrow" aria-hidden="true">
                  →
                </span>
              ) : null}
            </button>
          </form>
        )}
      </div>
    </article>
  )
}
